import {
  MOMENT_TYPES,
  isEolMoment,
  isPriceChangeStyleOfferMoment
} from 'utils/constants';
import { getYyyyMmDdInTimeZone } from './time';

/** Known moment type literals from {@link MOMENT_TYPES} (API may still send other strings). */
export type MomentProjectType =
  (typeof MOMENT_TYPES)[keyof typeof MOMENT_TYPES];

/** Shape returned by {@link getTimelineDisplayDates}; mirrors TimelineCard date chip logic. */
export type TimelineDisplayDates = Readonly<{
  start?: string;
  end?: string;
  month?: string;
  quarter?: string;
  endMonth?: string;
  dateStatus?: string;
}>;

export type MomentDates = Readonly<{
  targetStart?: string;
  targetEnd?: string;
  targetMonth?: string;
  targetQuarter?: string;
  targetEndMonth?: string;
  releaseDate?: string;
  releaseMonth?: string;
  releaseQuarter?: string;
  effectiveDate?: string;
  effectiveMonth?: string;
  effectiveQuarter?: string;
  customerNotificationDate?: string;
  customerNotificationMonth?: string;
  customerNotificationDateMonth?: string;
  customerNotificationQuarter?: string;
  customerNotificationDateQuarter?: string;
  targetDateStatus?: string;
  releaseDateStatus?: string;
  effectiveDateStatus?: string;
  customerNotificationDateStatus?: string;
}>;

/** Return `v` only if it is a YYYY-MM-DD ISO date; coarse strings like "Q2 2027" are discarded. */
export const isoDateOnly = (v?: string): string | undefined =>
  v && /^\d{4}-\d{2}-\d{2}/.test(v) ? v : undefined;

/** TBD / N/A status values — when set, concrete date fields must not be shown for that slot. */
export const isSpecialDateStatus = (status?: string): boolean =>
  status === 'TBD' || status === 'N/A';

/** DateComponent / quick-edit field shape (targetStart = ISO day, targetMonth / targetQuarter = coarse). */
export type DateComponentDateFields = Readonly<{
  targetStart?: string;
  targetMonth?: string;
  targetQuarter?: string;
}>;

export function getCustomerNotificationMonthQuarter(dates?: MomentDates) {
  return {
    month:
      dates?.customerNotificationMonth || dates?.customerNotificationDateMonth,
    quarter:
      dates?.customerNotificationQuarter ||
      dates?.customerNotificationDateQuarter
  };
}

/** PCO / phased offers / EOL stages: customer notification date present. */
export function hasCustomerNotificationDateFields(
  dates?: MomentDates
): boolean {
  const { month, quarter } = getCustomerNotificationMonthQuarter(dates);
  return !!(dates?.customerNotificationDate || month || quarter);
}

/** Customer notification chip / tooltip display (PCO-style field aliases). */
export function pickCustomerNotificationDisplayDates(
  dates?: MomentDates
): TimelineDisplayDates {
  const status = dates?.customerNotificationDateStatus;
  if (isSpecialDateStatus(status)) {
    return {
      start: undefined,
      end: undefined,
      month: undefined,
      quarter: undefined,
      endMonth: undefined,
      dateStatus: status
    };
  }

  const { month, quarter } = getCustomerNotificationMonthQuarter(dates);
  const rawCd = dates?.customerNotificationDate;
  const start = isoDateOnly(rawCd);

  return {
    start,
    end: undefined,
    month: month || (!start && rawCd ? rawCd : undefined),
    quarter,
    endMonth: undefined,
    dateStatus: status
  };
}

/** Effective date chip / tooltip display (PCO secondary / EOL effective stage). */
export function pickEffectiveDisplayDates(
  dates?: MomentDates
): TimelineDisplayDates {
  return {
    start: isoDateOnly(dates?.effectiveDate),
    end: undefined,
    month: dates?.effectiveMonth,
    quarter: dates?.effectiveQuarter,
    endMonth: undefined,
    dateStatus: dates?.effectiveDateStatus
  };
}

export function pickCustomerNotificationDateComponentFields(
  dates?: MomentDates
): DateComponentDateFields {
  const display = pickCustomerNotificationDisplayDates(dates);
  return {
    targetStart: display.start,
    targetMonth: display.month,
    targetQuarter: display.quarter
  };
}

export function pickEffectiveDateComponentFields(
  dates?: MomentDates
): DateComponentDateFields {
  const display = pickEffectiveDisplayDates(dates);
  return {
    targetStart: display.start,
    targetMonth: display.month,
    targetQuarter: display.quarter
  };
}

export function resolveCustomerNotificationRawValue(
  dates?: MomentDates
): string | undefined {
  const status = dates?.customerNotificationDateStatus;
  if (isSpecialDateStatus(status)) {
    return status;
  }

  const { month, quarter } = getCustomerNotificationMonthQuarter(dates);
  return (
    dates?.customerNotificationDate ||
    month ||
    quarter ||
    dates?.customerNotificationDateMonth ||
    dates?.customerNotificationDateQuarter
  );
}

/** True when customer notification should render (date fields and/or TBD/N/A status). */
export function hasCustomerNotificationDisplay(dates?: MomentDates): boolean {
  return (
    hasCustomerNotificationDateFields(dates) ||
    isSpecialDateStatus(dates?.customerNotificationDateStatus)
  );
}

export function resolveEffectiveRawValue(
  dates?: MomentDates
): string | undefined {
  return (
    dates?.effectiveDate || dates?.effectiveMonth || dates?.effectiveQuarter
  );
}

const ISO_MONTH_LABELS_UPPER = [
  'JAN',
  'FEB',
  'MAR',
  'APR',
  'MAY',
  'JUN',
  'JUL',
  'AUG',
  'SEP',
  'OCT',
  'NOV',
  'DEC'
] as const;

/**
 * Which date fields to show on the timeline / parking-lot chip, based on moment type and showOnTimeline.
 * Keep in sync with TimelineCard `getDisplayDates` behavior.
 *
 * @param projectType API `momentType` / card `projectType` (compare to {@link MOMENT_TYPES}).
 */
export function getTimelineDisplayDates(params: {
  dates?: MomentDates;
  /** Typically a {@link MomentProjectType} value from the API. */
  projectType: string;
  showOnTimeline: boolean;
  /** PCO flag — true for legacy PROMOS_OFFERS moments that follow Price Changes date logic. */
  pco?: boolean;
}): Readonly<TimelineDisplayDates> {
  const {
    dates,
    projectType,
    showOnTimeline: actualShowOnTimeline,
    pco
  } = params;

  const priceChangesEffectiveDateDisplay = (): TimelineDisplayDates =>
    pickEffectiveDisplayDates(dates);

  const customerNotificationTimelineDisplay = (): TimelineDisplayDates =>
    pickCustomerNotificationDisplayDates(dates);

  const { month: custNotifMonth, quarter: custNotifQuarter } =
    getCustomerNotificationMonthQuarter(dates);

  // Covers true PCO moments AND legacy PROMOS_OFFERS moments flagged as PCO (pco=true)
  if (isPriceChangeStyleOfferMoment(projectType, pco)) {
    if (actualShowOnTimeline) {
      return priceChangesEffectiveDateDisplay();
    }

    if (hasCustomerNotificationDisplay(dates)) {
      return customerNotificationTimelineDisplay();
    }
    // Legacy "Price Changes and Offers" moments (non-phased, pco:false) store the primary
    // (marketing) date under the generic target* fields, not customerNotificationDate*.
    // Without this fallback, both the primary and secondary cards ended up rendering the
    // effective date and read as duplicates on the timeline.
    const hasTargetDates = !!(
      dates?.targetStart ||
      dates?.targetMonth ||
      dates?.targetQuarter
    );
    if (hasTargetDates) {
      return {
        start: isoDateOnly(dates?.targetStart),
        end: isoDateOnly(dates?.targetEnd),
        month: dates?.targetMonth,
        quarter: dates?.targetQuarter,
        endMonth: dates?.targetEndMonth,
        dateStatus: dates?.targetDateStatus
      };
    }
    return priceChangesEffectiveDateDisplay();
  }

  // End of Life (EOL): customer notification vs effective by showOnTimeline (stages)
  if (isEolMoment(projectType)) {
    if (actualShowOnTimeline) {
      return priceChangesEffectiveDateDisplay();
    }
    if (hasCustomerNotificationDisplay(dates)) {
      return customerNotificationTimelineDisplay();
    }
    return priceChangesEffectiveDateDisplay();
  }

  const isPhasedPromosOffers =
    projectType === MOMENT_TYPES.PROMOS_OFFERS &&
    !!(dates?.customerNotificationDate || custNotifMonth || custNotifQuarter);

  if (isPhasedPromosOffers) {
    if (actualShowOnTimeline) {
      return {
        start: isoDateOnly(dates?.effectiveDate),
        end: undefined,
        month: dates?.effectiveMonth,
        quarter: dates?.effectiveQuarter,
        endMonth: undefined,
        dateStatus: dates?.effectiveDateStatus
      };
    }
    return customerNotificationTimelineDisplay();
  }

  if (!actualShowOnTimeline) {
    const hasTargetDates =
      dates?.targetStart || dates?.targetMonth || dates?.targetQuarter;

    if (hasTargetDates) {
      return {
        start: isoDateOnly(dates?.targetStart),
        end: isoDateOnly(dates?.targetEnd),
        month: dates?.targetMonth,
        quarter: dates?.targetQuarter,
        endMonth: dates?.targetEndMonth,
        dateStatus: dates?.targetDateStatus
      };
    }
    return {
      start:
        isoDateOnly(dates?.releaseDate) || isoDateOnly(dates?.effectiveDate),
      end: isoDateOnly(dates?.targetEnd),
      month: dates?.releaseMonth || dates?.effectiveMonth,
      quarter: dates?.releaseQuarter || dates?.effectiveQuarter,
      endMonth: dates?.targetEndMonth,
      dateStatus: dates?.releaseDateStatus || dates?.effectiveDateStatus
    };
  }

  return {
    start: isoDateOnly(dates?.releaseDate) || isoDateOnly(dates?.effectiveDate),
    end: isoDateOnly(dates?.targetEnd),
    month: dates?.releaseMonth || dates?.effectiveMonth,
    quarter: dates?.releaseQuarter || dates?.effectiveQuarter,
    endMonth: dates?.targetEndMonth,
    dateStatus: dates?.releaseDateStatus || dates?.effectiveDateStatus
  };
}

/**
 * Parking lot date-field selection rules.
 * Keeps timeline selection untouched while reusing the same compact chip formatting.
 *
 * Priority:
 * - Price-change-style moments: customer-notification -> target -> release -> effective
 * - Other moments: target -> release -> effective
 */
export function getParkingLotDisplayDates(params: {
  dates?: MomentDates;
  projectType: string;
  /** PCO flag — true for legacy PROMOS_OFFERS moments that follow Price Changes date logic. */
  pco?: boolean;
}): Readonly<TimelineDisplayDates> {
  const { dates, projectType, pco } = params;

  const isoOnly = (v?: string): string | undefined =>
    v && /^\d{4}-\d{2}-\d{2}/.test(v) ? v : undefined;

  const customerNotificationMonth =
    dates?.customerNotificationMonth || dates?.customerNotificationDateMonth;
  const customerNotificationQuarter =
    dates?.customerNotificationQuarter ||
    dates?.customerNotificationDateQuarter;

  const customerNotificationDisplay = (): TimelineDisplayDates =>
    pickCustomerNotificationDisplayDates(dates);

  const targetDisplay = (): TimelineDisplayDates => ({
    start: isoOnly(dates?.targetStart),
    end: isoOnly(dates?.targetEnd),
    month: dates?.targetMonth,
    quarter: dates?.targetQuarter,
    endMonth: dates?.targetEndMonth,
    dateStatus: dates?.targetDateStatus
  });

  const releaseDisplay = (): TimelineDisplayDates => ({
    start: isoOnly(dates?.releaseDate),
    end: isoOnly(dates?.targetEnd),
    month: dates?.releaseMonth,
    quarter: dates?.releaseQuarter,
    endMonth: dates?.targetEndMonth,
    dateStatus: dates?.releaseDateStatus
  });

  const effectiveDisplay = (): TimelineDisplayDates => ({
    start: isoOnly(dates?.effectiveDate),
    end: undefined,
    month: dates?.effectiveMonth,
    quarter: dates?.effectiveQuarter,
    endMonth: undefined,
    dateStatus: dates?.effectiveDateStatus
  });

  const hasCustomerNotification = !!(
    dates?.customerNotificationDate ||
    customerNotificationMonth ||
    customerNotificationQuarter ||
    dates?.customerNotificationDateStatus
  );
  const hasTarget = !!(
    dates?.targetStart ||
    dates?.targetMonth ||
    dates?.targetQuarter ||
    dates?.targetDateStatus
  );
  const hasRelease = !!(
    dates?.releaseDate ||
    dates?.releaseMonth ||
    dates?.releaseQuarter ||
    dates?.releaseDateStatus
  );

  if (isPriceChangeStyleOfferMoment(projectType, pco)) {
    if (hasCustomerNotification) return customerNotificationDisplay();
    if (hasTarget) return targetDisplay();
    if (hasRelease) return releaseDisplay();
    return effectiveDisplay();
  }

  // EOL parking-lot cards (showOnRoadmap/showOnTimeline false): customer notification
  // including TBD/N/A — must not fall through to effectiveDate (ARM-1897).
  if (isEolMoment(projectType)) {
    if (hasCustomerNotificationDisplay(dates)) {
      return customerNotificationDisplay();
    }
    return effectiveDisplay();
  }

  if (hasTarget) return targetDisplay();
  if (hasRelease) return releaseDisplay();
  return effectiveDisplay();
}

export type IsoDateOnlyParts = Readonly<{
  year: number;
  month: number;
  day: number;
}>;

/** Parse YYYY-MM-DD prefix as calendar components (no timezone shift). */
export function parseIsoDateOnlyParts(
  isoDate: string
): IsoDateOnlyParts | null {
  const match = isoDate.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  if (month < 1 || month > 12 || day < 1 || day > 31) return null;

  return { year, month, day };
}

/** Normalize parsed parts to YYYY-MM-DD for lexicographic comparison. */
export function isoDateOnlyToYmd(parts: IsoDateOnlyParts): string {
  return `${parts.year}-${String(parts.month).padStart(2, '0')}-${String(
    parts.day
  ).padStart(2, '0')}`;
}

/**
 * True when a calendar YYYY-MM-DD is strictly before today in the viewer's
 * local timezone (matches SimpleDateField overdue semantics).
 */
export function isIsoDateOnlyBeforeToday(isoDate: string): boolean {
  const parts = parseIsoDateOnlyParts(isoDate);
  if (!parts) return false;
  return isoDateOnlyToYmd(parts) < getYyyyMmDdInTimeZone();
}

/** Format YYYY-MM-DD (prefix) to compact chip text like `24 APR`. */
export function formatIsoDateAsDayMonthUpper(isoDate: string): string {
  const parts = parseIsoDateOnlyParts(isoDate);
  if (!parts) return isoDate;

  const monthIndex = parts.month - 1;
  if (monthIndex < 0 || monthIndex > 11) return isoDate;
  return `${parts.day} ${ISO_MONTH_LABELS_UPPER[monthIndex]}`;
}

/**
 * Single-line label for parking-lot / compact chip from {@link TimelineDisplayDates}.
 * Month/quarter strings from the API are typically `"Jun 2026"` or `"Q2 2026"`; we use the first
 * whitespace-separated token then uppercase (matches TimelineCard compact chip behavior).
 */
export function formatTimelineDateChipLabel(
  displayDates: TimelineDisplayDates
): string {
  if (isSpecialDateStatus(displayDates.dateStatus)) {
    return displayDates.dateStatus!;
  }
  if (displayDates.start) {
    return formatIsoDateAsDayMonthUpper(displayDates.start);
  }
  if (displayDates.month) {
    return displayDates.month.split(' ')[0].toUpperCase();
  }
  if (displayDates.quarter) {
    return displayDates.quarter.split(' ')[0].toUpperCase();
  }
  if (displayDates.dateStatus) {
    return displayDates.dateStatus;
  }
  return 'TBD';
}
